import { aspectRatioConfig } from 'core/config/AspectRatioConfig';
import { AspectRatio } from 'core/constants/enums/AspectRatioEnum.ts';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import { tabletBreakpoint } from 'core/layouts/ui-blocks/ReusableStyles';

const landscapeAspectRatio = aspectRatioConfig[AspectRatio.LANDSCAPE].aspectRatio;
const portraitAspectRatio = aspectRatioConfig[AspectRatio.PORTRAIT].aspectRatio;

const tabletPadding = '10px';
const totalTabletPadding = `(2 * ${tabletPadding})`;

const arrowButtonDimension = '40px';
const arrowButtonBounding = '16px';
const totalArrowButtonsBounding = `((2 * ${arrowButtonDimension}) + (2 * ${arrowButtonBounding}))`;
const actionMenuWidth = '192px';

const maxHeight = `calc(100vh - ${totalTabletPadding})`;
const maxWidth = `calc(100vw - ${totalTabletPadding} - ${totalArrowButtonsBounding} - ${actionMenuWidth})`;
const imageDimension = `min(${maxWidth}, ${maxHeight})`;
const landscapeImageDimension = `min(${maxWidth}, calc(${landscapeAspectRatio} * (${maxHeight})))`;
const portraitImageDimension = `min(${maxWidth}, calc(${portraitAspectRatio} * (${maxHeight})))`;

const mobileHPadding = '16px';
const mobileVPadding = '40px';
const totalMobileHPadding = `(2 * ${mobileHPadding})`;
const totalMobileVPadding = `(2 * ${mobileVPadding})`;
const actionMenuWidthMobile = '64px';

const maxHeightMobile = `calc(100svh - ${totalMobileVPadding})`;
const maxWidthMobile = `calc(100svw - ${totalMobileHPadding} - ${actionMenuWidthMobile})`;
const imageDimensionMobile = `min(${maxWidthMobile},${maxHeightMobile})`;
const landscapeImageMobileDimension = `min(${maxWidthMobile}, calc(${landscapeAspectRatio} * (${maxHeightMobile})))`;
const portraitImageMobileDimension = `min(${maxWidthMobile}, calc(${portraitAspectRatio} * (${maxHeightMobile})))`;

export const DEFAULT_IMAGE_SIZE = 1024;

const maxWidthStyles = {
	xs: maxWidthMobile,
	[tabletBreakpoint]: maxWidth,
};

const maxHeightStyles = {
	xs: maxHeightMobile,
	[tabletBreakpoint]: maxHeight,
};

const breakpoints = ['xs', tabletBreakpoint];

const imageDimensionStyles = (h, w) => (breakpoint) => ({
	'--varMaxHeight': maxHeightStyles[breakpoint],
	'--varMaxWidth': maxWidthStyles[breakpoint],
	'--varHWRatio': h / w,
	'--varWHRatio': w / h,
	'--varWidth': 'min(var(--varMaxWidth), calc(var(--varMaxHeight) * var(--varWHRatio)))',
	'--varHeight': 'calc(var(--varWidth) * var(--varHWRatio))',
});
export const getImageDisplayStyles = (h = DEFAULT_IMAGE_SIZE, w = DEFAULT_IMAGE_SIZE) =>
	breakpoints.reduce((acc, breakpoint) => {
		acc[breakpoint] = {
			...imageDimensionStyles(h, w)(breakpoint),
			height: 'var(--varHeight)',
			width: 'var(--varWidth)',
		};
		return acc;
	}, {});

export const getMaxContainerHeightStyles = (h = DEFAULT_IMAGE_SIZE, w = DEFAULT_IMAGE_SIZE) =>
	breakpoints.reduce((acc, breakpoint) => {
		acc[breakpoint] = {
			...imageDimensionStyles(h, w)(breakpoint),
			maxHeight: 'var(--varHeight)',
			height: 'var(--varHeight)',
		};
		return acc;
	}, {});

const expandImageModalStyles = {
	modal: {
		'& .MuiModal-backdrop': {
			backgroundColor: colorPalette.modalBackdrop,
		},
	},
	modalWrapper: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',

		outline: 'none',
	},
	modalBody: {
		height: { [tabletBreakpoint]: '100vh', xs: '100svh' },
		width: { [tabletBreakpoint]: '100vw', xs: '100svw' },

		p: { xs: `${mobileVPadding} ${mobileHPadding}`, [tabletBreakpoint]: tabletPadding },

		display: 'flex',
		alignItems: 'center',
		gap: arrowButtonBounding,

		position: 'relative',
	},
	crossButton: {
		position: 'absolute',
		top: { xs: mobileHPadding, [tabletBreakpoint]: tabletPadding },
		right: { xs: mobileHPadding, [tabletBreakpoint]: tabletPadding },

		minWidth: 'unset',
		padding: 0,

		zIndex: 1,

		'& svg': {
			stroke: colorPalette.light2,

			height: { xs: '24px', [tabletBreakpoint]: '32px' },
			width: 'auto',
		},
		'&.Mui-disabled': {
			'& svg': {
				stroke: colorPalette.light2,
			},
		},
	},

	arrowButton: {
		position: { xs: 'fixed', [tabletBreakpoint]: 'relative' },

		cursor: 'pointer',
		zIndex: 2,

		'& svg': { height: { xs: '24px', [tabletBreakpoint]: '40px' }, width: 'auto' },
	},

	contentHeightInfoShown: {
		height: '100%',
		maxHeight: {
			xs: `calc(100vh - ${mobileVPadding})`,
			[tabletBreakpoint]: `calc(100vh - ${totalTabletPadding})`,
		},
	},

	content: {
		flexGrow: 1,
		flexDirection: 'row',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',

		overflow: { xs: 'auto', [tabletBreakpoint]: 'hidden' },
	},

	image: {
		overflow: 'hidden',
		maxHeight: {
			xs: `calc(100vh - ${mobileVPadding})`,
			[tabletBreakpoint]: `calc(100vh - ${totalTabletPadding})`,
		},
		maxWidth: {
			xs: `calc(100vw - ${mobileHPadding})`,
			[tabletBreakpoint]: `calc(100vw - ${totalTabletPadding})`,
		},
		borderRadius: '4px',
		position: 'relative',

		'& img': {
			objectFit: 'contain',
		},
	},
	squareImg: {
		width: { xs: imageDimensionMobile, [tabletBreakpoint]: imageDimension },
		height: { xs: imageDimensionMobile, [tabletBreakpoint]: imageDimension },
		'& img': { aspectRatio: 1 },
	},
	portraitImg: {
		width: { xs: portraitImageMobileDimension, [tabletBreakpoint]: portraitImageDimension },
		height: {
			xs: `calc(${landscapeAspectRatio} * ${portraitImageMobileDimension})`,
			[tabletBreakpoint]: `calc(${landscapeAspectRatio} * ${portraitImageDimension})`,
		},
		'& img': { aspectRatio: portraitAspectRatio },
	},
	landscapeImg: {
		height: {
			xs: `calc(${portraitAspectRatio} * ${landscapeImageMobileDimension})`,
			[tabletBreakpoint]: `calc(${portraitAspectRatio} * ${landscapeImageDimension})`,
		},
		width: { xs: landscapeImageMobileDimension, [tabletBreakpoint]: landscapeImageDimension },
		'& img': { aspectRatio: landscapeAspectRatio },
	},

	actionsMenuCont: {
		minWidth: {
			xs: '48px',
			[tabletBreakpoint]: '156px',
		},
		overflow: 'auto',
	},

	buttonsCont: {
		position: {
			xs: 'fixed',
			[tabletBreakpoint]: 'absolute',
		},

		padding: '8px 16px',

		display: 'flex',
		alignItems: 'center',
		gap: {
			xs: '12px',
			[tabletBreakpoint]: '16px',
		},
		width: '100%',
		flexWrap: 'wrap',
	},
	exportButtonsCont: {
		bottom: 0,
		left: 0,
	},

	topButtonsCont: {
		top: 0,
		left: 0,
	},
	seedCont: {
		top: 0,
		right: 0,
	},
};

export { expandImageModalStyles };
